import { useTranslate } from "@tolgee/react";
import { KrwSubtext, useIsKoreanLocale } from "#/components/krw-subtext";
import { PercentageSlider } from "#/components/ui/percentage-slider.tsx";
import { cn } from "#/lib/utils";
import { toNumber } from "../../lib/formatters.ts";
import type { useOrderFormState } from "./hooks/use-order-form-state.ts";
import { QuickPriceButton } from "./quick-price-button.tsx";
import { SizeInput } from "./size-input.tsx";
import { TriggerPriceField } from "./trigger-price-field.tsx";
import { TwapFields } from "./twap-fields.tsx";
import { sanitizeNumericInput } from "./utils/sanitize-numeric-input.ts";

type OrderFormState = ReturnType<typeof useOrderFormState>;

export type OrderFormFieldsInput = Pick<
	OrderFormState,
	| "price"
	| "setPrice"
	| "amount"
	| "setAmount"
	| "quoteAmount"
	| "setQuoteAmount"
	| "amountPercentage"
	| "setAmountPercentage"
	| "amountInputMode"
	| "setAmountInputMode"
	| "side"
	| "showTriggerPrice"
	| "showLimitPrice"
	| "showTwapFields"
	| "autoQuoteActive"
	| "orderValue"
>;

type OrderFormFieldsProps = {
	form: OrderFormFieldsInput;
	coin: string;
	szDecimals: number;
	quickPrice?: string;
	showPercentageSlider?: boolean;
};

const inputClass =
	"w-full bg-transparent text-label1-bold text-trade-text outline-none placeholder:text-trade-text-faint sm:text-body2-bold";

const fieldClass =
	"flex items-center gap-1.5 rounded-[8px] border border-trade-border-subtle bg-trade-surface-inset px-2.5 py-1.5 sm:gap-2 sm:rounded-[10px] sm:px-3.5 sm:py-2";

const PRICE_MAX_DECIMALS = 8;

export function OrderFormFields({
	form,
	coin,
	szDecimals,
	quickPrice,
	showPercentageSlider = true,
}: OrderFormFieldsProps) {
	const { t } = useTranslate();
	const isKorean = useIsKoreanLocale();
	const labelClassName = "whitespace-nowrap text-label2 sm:text-body2";
	const priceNumeric = toNumber(form.price);
	const showPriceKrw = isKorean && priceNumeric > 0;

	return (
		<div className="flex flex-col gap-1.5 sm:gap-2">
			{form.showTriggerPrice && <TriggerPriceField />}

			{form.showLimitPrice && (
				<div className="flex flex-col gap-0.5">
					<label className={fieldClass}>
						<div className="flex w-full items-center gap-2 sm:gap-3">
							<p className={labelClassName}>
								<span className="">{t("form.price")}</span>
							</p>
							<div className={'flex-1'}>
								<input
									type="text"
									inputMode="decimal"
									className={cn(inputClass, "text-right")}
									value={form.price}
									onChange={(e) =>
										form.setPrice(
											sanitizeNumericInput(e.target.value, PRICE_MAX_DECIMALS),
										)
									}
									placeholder="0"
									readOnly={form.autoQuoteActive}
								/>
								{showPriceKrw && (
									<div className="flex justify-end">
										<KrwSubtext
											usd={priceNumeric}
											className="text-caption1 sm:text-label2"
										/>
									</div>
								)}
							</div>
						</div>
						<QuickPriceButton
							side={form.side}
							price={quickPrice}
							disabled={form.autoQuoteActive}
							onApplyPrice={form.setPrice}
						/>
					</label>

				</div>
			)}

			{form.showTwapFields && <TwapFields />}

			<SizeInput
				amount={form.amount}
				quoteAmount={form.quoteAmount}
				coin={coin}
				amountInputMode={form.amountInputMode}
				onAmountInputModeChange={form.setAmountInputMode}
				onAmountChange={form.setAmount}
				onQuoteAmountChange={form.setQuoteAmount}
				szDecimals={szDecimals}
			/>

			{showPercentageSlider && (
				<PercentageSlider
					value={form.amountPercentage}
					onChange={form.setAmountPercentage}
				/>
			)}
		</div>
	);
}
